/**
 * TimelineView — chronological stream of run events.
 * Spec 17 P4.3, paired with SpineTimelineToggle (P4.2).
 *
 * Renders one continuous chronological surface (oldest → newest):
 * persisted chat history merges with live EventLog rows, grouped by
 * calendar day under sticky day banners ("Today" / "Yesterday" /
 * weekday / date). Scrolling to the top pages older history in via
 * ``onLoadOlder`` (IntersectionObserver sentinel + scroll-anchor
 * preservation so the viewport never jumps on prepend).
 *
 * Event-payload drill-in (e.g. showing a regen's before/after diff,
 * a web search's query + hits) is deferred — P6 will add it as part
 * of /replay and /export where the payloads become load-bearing.
 * For now, writers get the chronology + a title that tells them
 * what happened; the full payload is audit-export territory.
 *
 * SRP: presentational. Takes ``entries`` + an optional filter
 * section label for the empty-state copy. The shell owns the log
 * and filtering.
 *
 * OCP: adding a new EventKind requires ONE entry in
 * ``formatEventTitle``'s switch. Nothing else in the component
 * render changes.
 *
 * # DECISION: empty state reads "No events yet — timeline fills as
 * #   the run progresses" rather than a generic "(empty)".
 * # WHY: pre-run and very-early-run states are both common; the
 * #      honest message is "just wait." A writer seeing an empty
 * #      timeline with the run's first section drafting should feel
 * #      "fine, it's coming" — not "something broke."
 * # DATE: 2026-04-21
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { type TimelineItem, groupItemsByDay } from "../chatDayGrouping";
import { ActionGroup, StepRow } from "./ActionGroup";
import type { EventKind, EventLogEntry } from "../eventLog";
import type { SetupDocumentInfo } from "../documentIdentity";
import type { ChatMessage, RunCompleteSummary } from "../messageProjection";
import { RUN_STARTED_TEXT, parseRunCompleteText, projectToMessages, withoutDraftEchoes } from "../messageProjection";
import {
  type BatchMark,
  type FoldProbe,
  type RunStep,
  foldRunRows,
  pairRunBatches,
  runStepFromMessage,
} from "../runCapsuleModel";
import { type SetupCommand, type SetupProbe, foldSetupRows, setupProbeForEntry, setupProbeForMessage } from "../setupCardModel";
import type { PendingApproval } from "../state";
import { brandColors, fonts, inkColors, spacing, statusColors, surfaceColors, typeScale } from "../tokens";
import AsthraBubbleSurface from "./AsthraBubbleSurface";
import InlineApprovalRow from "./InlineApprovalRow";
import MessageBubble from "./MessageBubble";
import RunRibbon from "./RunRibbon";
import SetupCard from "./SetupCard";
import { useEditSteps } from "../services/missingInfoProgress";
import { OpenImpactedSectionsButton } from "../source-impact/OpenImpactedSectionsButton";
import { impactDocumentFromToolStep } from "../source-impact/sourceImpactModel";

/** How long after the last scroll event the sticky day banner stays
 *  up before fading out. Long enough to read the label after a flick
 *  settles, short enough that it never reads as permanent chrome. */
const DAY_BANNER_IDLE_MS = 2500;

/** Grace window after an app-driven scroll during which scroll events
 *  are attributed to the app, not the writer, and so don't reveal the
 *  day banner. Covers a native smooth-scroll's animation (~300-500ms)
 *  with headroom; a longer one may flash the banner mid-flight, which
 *  self-corrects on the next idle tick. */
const PROGRAMMATIC_SCROLL_WINDOW_MS = 800;

// ---------------------------------------------------------------------------
// Pure helpers — testable in isolation
// ---------------------------------------------------------------------------

/**
 * Short time-of-day label for the row's left column ("2:14pm").
 * Accepts a timestamp in epoch ms; pure, deterministic given a
 * fixed locale (uses Intl).
 */
export function formatEventTime(ts: number): string {
  const d = new Date(ts);
  // Lowercase am/pm + no leading zero on hour matches the scenes file's
  // ticker cadence ("2:14pm", not "02:14 PM"). Uses en-US to keep the
  // format stable across machines.
  const hour12 = d.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return hour12.replace(/\s?(AM|PM)/i, (_, m) => m.toLowerCase());
}

/**
 * Human-readable title per event. One line, no wrapping, safe to
 * render inside a 440px pane. Section references use ``§X`` (writer
 * vocabulary, not internal module codes) — resolved by the caller
 * if needed; when sectionId is the internal id (e.g. "CON-401"), we
 * surface it as-is and let the shell decide whether to resolve.
 *
 * The formatter is exhaustive over EventKind — adding a new kind
 * fails to compile here before anything else notices.
 */
export function formatEventTitle(entry: EventLogEntry): string {
  // Phase 9 — prefer the writer-facing label captured at the write
  // boundary (``§${number}``); fall back to the raw sectionId only
  // if no label was resolved by the producer. Keeps backend
  // identifiers (``"FRM-607"``) out of writer-visible chrome.
  const sectionDisplay = entry.sectionLabel ?? entry.sectionId;
  const section = sectionDisplay ? ` · ${sectionDisplay}` : "";
  const p = entry.payload;
  switch (p.kind) {
    case "draft": {
      const wc = p.wordCount ? ` · ${p.wordCount} words` : "";
      return `Drafted${section}${wc}`;
    }
    case "adhoc_draft": {
      const wc = p.wordCount ? ` · ${p.wordCount} words` : "";
      return `Ad-hoc draft${section}${wc} (awaiting Insert)`;
    }
    case "edit_proposal":
      return `Edit proposal${section} — ${p.decision}`;
    case "regen":
      return `Regen${section} attempt ${p.attempt} ${p.outcome}`;
    case "cite":
      return `Citation swap${section}`;
    case "web":
      return `Web search · ${p.query}`;
    case "prompt":
      return `"${truncate(p.text, 60)}"`;
    case "askwhy":
      // Spec 29 — Timeline is the chat surface. The writer's
      // question already lives in the preceding ``prompt`` row;
      // the askwhy title previews the AGENT's answer to avoid
      // redundant rendering. Expanded detail (AskWhyDetail) still
      // shows both question and answer for audit.
      if (p.answer && p.answer.length > 0) {
        return truncate(p.answer, 80);
      }
      return `Thinking…${section}`;
    case "word":
      return `Manual Word edit${section}`;
    case "library": {
      const range = p.fromVersion && p.toVersion ? ` · ${p.fromVersion} → ${p.toVersion}` : "";
      return `Library updated${range}`;
    }
    case "approval":
      return `Approval ${p.decision}${section}`;
    case "run_start":
      // Phase 8.5 — run lifecycle bracket; chat surface uses
      // messageProjection's "Run started" system note for prose.
      // The TimelineRowDetail title surfaces the same vocabulary.
      return "Run started";
    case "run_complete": {
      const drafted = p.draftedSections;
      const total = p.totalSections;
      if (drafted != null && total != null) {
        return `Run complete — ${drafted}/${total} drafted`;
      }
      return "Run complete";
    }
    // Phase 9.5 (Bundle A) — pre-run conversational picks. Title
    // mirrors the chat copy without the next-step pointer.
    case "doctype_selected":
      return `Selected ${p.label}`;
    case "library_selected":
      return p.created ? `Created library "${p.libraryName}"` : `Selected library "${p.libraryName}"`;
    // Phase 9.5 (Bundle B+D) — agent_note titles surface a short
    // teaser of the suggestion text. Truncation prevents
    // multi-line rows in the timeline.
    case "agent_note":
      return truncate(p.text, 80);
    // Regulatory audit — surface the progress/result line.
    case "audit":
      return truncate(p.message, 80);
    case "activity":
      return truncate(p.text, 80);
    // Chat tool step — mirror the chat row's "name · summary" shape.
    case "chat_tool_step":
      return truncate(p.inputSummary || p.resultPreview || p.tool, 80);
    default:
      return assertUnreachableKind(p);
  }
}

/** Single-character glyph per actor — small + scannable. */
export function actorGlyph(actor: "asthra" | "you"): string {
  return actor === "you" ? "»" : "●";
}

/** Live-vs-persisted dedup key. Mirrors chatHistoryWiring.mergeKey —
 *  keep the two in sync or persisted rows will double-render. */
function timelineDedupKey(message: ChatMessage): string {
  const base = `${message.at}:${message.role}`;
  return message.role === "tool" ? `${base}:${message.name}` : base;
}

function truncate(s: string, n: number): string {
  const cleaned = s.replace(/\s+/g, " ").trim();
  return cleaned.length > n ? `${cleaned.slice(0, n - 1)}…` : cleaned;
}

function assertUnreachableKind(p: never): never {
  throw new Error(`[TimelineView] unhandled event payload kind: ${String(p)}`);
}

/**
 * Decide whether a newly-arrived entry should be auto-expanded in
 * the timeline. Extracted as a pure helper so the branches are
 * testable without a React tree.
 *
 * # DECISION: only ``askwhy`` kind auto-expands on arrival; every
 * #   other kind (draft / regen / web / cite / prompt / word /
 * #   library / approval) lands silently as a collapsed row and
 * #   expands on demand.
 * # WHY: askwhy events are the agent's replies to /web,
 * #      /citations, and freeform chat — the writer actively wants
 * #      to read the body. Other kinds land in streams (e.g. a
 * #      38-section run emits dozens of draft + regen events);
 * #      auto-expanding each one would jump the layout constantly
 * #      + cover the timeline in expanded detail the writer hasn't
 * #      asked for. Narrowing to askwhy is the honest "auto-show
 * #      only what's conversational" rule.
 * # DATE: 2026-04-23
 *
 * # DECISION: only once per unique id — writer's explicit collapse
 * #   (chevron click or Escape) sticks.
 * # WHY: without the one-shot guard, every re-render would re-run
 * #      the effect against the same newest entry and re-expand it
 * #      even after the writer closed it. The caller carries
 * #      ``lastAutoExpandedId`` across renders (via useRef) so the
 * #      helper stays pure.
 * # DATE: 2026-04-23
 *
 * Pure; no side effects; no React; no hooks.
 */
export function shouldAutoExpandEntry(newest: EventLogEntry | undefined, lastAutoExpandedId: string | null): boolean {
  if (!newest) return false;
  if (newest.payload.kind !== "askwhy") return false;
  if (newest.id === lastAutoExpandedId) return false;
  return true;
}

/**
 * Content-aware greeting for the Loom/Thread idle state. Spec 29
 * Phase 3 follow-up.
 *
 * Adapts copy to the Word document's classification:
 *   - empty / unavailable / loading → "Welcome"
 *   - has-content, unknown doctype → "I see content here; start
 *     fresh, or post-run chat to ask about it"
 *   - has-content + CER/CSR/PSUR → addresses the doctype by name
 *     and frames the likely next actions
 *
 * Pure so copy changes are pinned by tests without a React tree.
 *
 * # DECISION: pack the regulatory AI-drafted disclaimer into the
 * #   greeting rather than a separate always-visible banner.
 * # WHY: the banner was ~20px of always-on chrome in Loom/Spine —
 * #      retired per the chrome-reduction goal. The disclaimer is
 * #      still important for regulated-writing users; packing it
 * #      into the greeting (visible on first turn, not every
 * #      subsequent render) gives them the same information at
 * #      the right moment without the persistent chrome cost.
 * # DATE: 2026-04-23
 *
 * # DECISION: greeting is text-only (no clickable chips).
 * # WHY: per the user's Spec 29 locked decision — "not sure if we
 * #      need cards. maybe the chat itself would be the primary
 * #      experience where the agent guides the user that if he
 * #      wants to generate a document…" Keeps the first-impression
 * #      surface very clean.
 * # DATE: 2026-04-23
 */

import type { DocumentClassification } from "../documentClassifier";

/** Shared disclaimer appended to every greeting variant — the
 *  regulatory copy that retired with DisclosureBanner. Writers
 *  see it on their first turn; subsequent renders (once events
 *  land) hide the greeting so it doesn't repeat. */
const AI_DISCLAIMER =
  "Drafts are AI-generated; every claim cites a source. You review and sign off before anything ships.";

export function buildIdleGreeting(classification: DocumentClassification | null): string {
  // Spec: when null is passed (classification is ready but being
  // hidden by the new checklist/card components), or when unavailable
  // (loading / Office.js failed), fall back to the basic empty greeting.
  if (!classification) {
    // Fallback copy for when classification is unavailable or suppressed
    // by the new blank-doc onboarding. The checklist and context card
    // are the primary surfaces; this is shown only during classification load.
    return [
      "Welcome.",
      "I'm your AI co-author. Start a new document to get going, or ask me a question.",
      AI_DISCLAIMER,
    ].join("\n\n");
  }

  // Note: classification.state "empty" and "has-content" are now handled
  // by IdleChecklist and ContextCard components respectively. This function
  // is only called when classification is null (loading/unavailable).
  // Fallback to the basic greeting for safety.
  return [
    "Welcome.",
    "I'm your AI co-author. Start a new document to get going, or ask me a question.",
    AI_DISCLAIMER,
  ].join("\n\n");
}

// ---------------------------------------------------------------------------
// Idle greeting — interactive onboarding message (formerly the standalone
// IdleChecklist / ContextCard components, inlined here so the whole greeting
// flow lives in one file alongside ``buildIdleGreeting``).
//
// ``IdleGreeting`` renders ONE persistent chat message that adapts to the
// document's classification:
//   - empty doc      → 3-step guided draft checklist (doctype → library → draft)
//   - has-content doc → capability card ("Looks like you already have a
//                       document here") with ask / fresh-draft actions
//
// It is presentational and stateless: every step/branch is derived from the
// props the shell passes in (selection booleans + callbacks). The shell still
// owns the handlers and the decision of WHEN to mount it (during loading it
// passes the plain ``buildIdleGreeting`` text instead).
// ---------------------------------------------------------------------------

const useIdleGreetingStyles = makeStyles({
  // Shared heading used by both branches.
  greetHi: {
    fontWeight: 700,
    color: "var(--ink)",
    fontSize: "13.5px",
    display: "block",
    marginBottom: "4px",
  },
  // --- Checklist (empty doc) ---
  intro: {
    fontSize: "12px",
    color: "var(--ink-muted)",
    lineHeight: 1.45,
  },
  stepsContainer: {
    marginTop: "12px",
  },
  step: {
    display: "flex",
    gap: "11px",
    alignItems: "flex-start",
    padding: "11px 0",
    borderBottom: "1px solid var(--line)",
    "&:last-child": {
      borderBottom: "none",
      paddingBottom: "2px",
    },
  },
  stepN: {
    width: "22px",
    height: "22px",
    borderRadius: "999px",
    flexShrink: 0,
    fontSize: "11px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "1px",
  },
  stepNActive: {
    background: brandColors.primary,
    color: "var(--on-brand)",
    boxShadow: "0 0 0 3px #F3EDFC",
  },
  stepNTodo: {
    background: "var(--brand-wash)",
    color: "var(--ink-subtle)",
    border: "1px solid var(--line)",
  },
  stepNDone: {
    background: "#26DF9B",
    color: "#fff",
  },
  stepContent: {
    flex: 1,
  },
  stepH: {
    fontSize: "12.5px",
    fontWeight: 700,
    color: "var(--ink)",
  },
  stepHDim: {
    color: "var(--ink-subtle)",
    fontWeight: 600,
  },
  stepSub: {
    fontSize: "11px",
    color: "var(--ink-muted)",
    lineHeight: 1.4,
    marginTop: "2px",
  },
  // Completed-step value rendered as a subtle bordered pill that stays
  // editable: the whole chip (and its pencil icon) re-opens the picker.
  // Keeps the green "selected" colour while signalling it's editable.
  stepPickChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "4px",
    padding: "3px 8px 3px 9px",
    borderRadius: "999px",
    border: "1px solid color-mix(in srgb, var(--success) 35%, transparent)",
    background: "var(--success-soft)",
    color: "var(--success)",
    fontSize: "11px",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: fonts.body,
    transition: "background 120ms ease, border 120ms ease, box-shadow 120ms ease",
    "&:hover": {
      background: "var(--success-soft)",
      border: "1px solid #26DF9B",
    },
    "&:active": {
      background: "var(--success-soft)",
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: "0 0 0 3px rgba(38, 223, 155, 0.28)",
    },
  },
  stepPickValue: {
    lineHeight: 1.2,
  },
  stepPickIcon: {
    width: "11px",
    height: "11px",
    flexShrink: 0,
    opacity: 0.8,
  },
  stepGo: {
    background: brandColors.primary,
    color: "var(--on-brand)",
    borderRadius: "6px",
    padding: "5px 11px",
    fontSize: "11.5px",
    fontWeight: 700,
    marginTop: "7px",
    display: "inline-block",
    cursor: "pointer",
    border: "none",
    fontFamily: fonts.body,
    "&:hover": {
      background: "#3A2567",
    },
  },
  // --- Context card (has-content doc) ---
  bodyText: {
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "var(--ink)",
  },
  buttonContainer: {
    marginTop: "12px",
  },
  primaryBtn: {
    display: "inline-block",
    background: brandColors.primary,
    color: "var(--on-brand)",
    border: "none",
    borderRadius: "7px",
    padding: "8px 13px",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: fonts.body,
    marginRight: "7px",
    marginTop: "11px",
    "&:hover": {
      background: "#3A2567",
    },
  },
  secondaryBtn: {
    display: "inline-block",
    background: "var(--surface-raised)",
    color: brandColors.primary,
    border: "1px solid var(--line)",
    borderRadius: "7px",
    padding: "8px 13px",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: fonts.body,
    marginTop: "11px",
    "&:hover": {
      background: "var(--brand-wash)",
    },
  },
  disc: {
    fontSize: "10.5px",
    color: "var(--ink-subtle)",
    lineHeight: 1.45,
    marginTop: "13px",
  },
});

/** Small pencil/edit glyph. Inherits ``currentColor`` so it tracks
 *  the chip's green text and hover state. */
function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M11.4 2.1a1.3 1.3 0 0 1 1.85 0l.65.65a1.3 1.3 0 0 1 0 1.85l-7.2 7.2-2.85.65.65-2.85 7.2-7.2Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface IdleGreetingProps {
  /** Document classification driving which branch renders. */
  classification: DocumentClassification;
  // Checklist (empty doc) inputs.
  hasDoctype: boolean;
  hasLibrary: boolean;
  doctypeLabel: string | null;
  libraryName: string | null;
  onPickDoctype(): void;
  onPickLibrary(): void;
  onReviewAndStart(): void;
  // Context card (has-content doc) inputs.
  onAskAboutDocument(): void;
  onStartFreshDraft(): void;
  /**
   * Whether to render the "Ask about this document" CTA.
   *
   * Driven by the ``show.incomplete-actions`` feature flag (resolved in
   * VariantCShell). The handler currently only prefills the composer with
   * an empty string, so to a user the button reads as doing nothing —
   * AppSource certification rejects non-functional buttons on sight.
   *
   * Defaults to ``false`` so the button is hidden unless a flag explicitly
   * turns it on. That polarity is deliberate: ``useFlag`` returns false
   * while flags are loading, on fetch error, and for unknown keys, so a
   * "hide when true" flag would flash the button at every user before
   * resolution. "Show when true" degrades to hidden in all three cases.
   */
  showAskAboutDocument?: boolean;
}

/**
 * Interactive idle greeting. Picks the checklist (empty) or the context
 * card (has-content) based on ``classification.state``. Both branches were
 * previously their own components (IdleChecklist / ContextCard); inlined
 * here to keep the greeting flow centralized in TimelineView.
 */
export function IdleGreeting({
  classification,
  hasDoctype,
  hasLibrary,
  doctypeLabel,
  libraryName,
  onPickDoctype,
  onPickLibrary,
  onReviewAndStart,
  onAskAboutDocument,
  onStartFreshDraft,
  showAskAboutDocument = false,
}: IdleGreetingProps) {
  const styles = useIdleGreetingStyles();

  // --- Has-content doc → capability card ---
  if (classification.state === "has-content") {
    const probableDocType = classification.probableDocType;
    const greeting =
      probableDocType === "CER"
        ? "This reads like a Clinical Evaluation Report."
        : probableDocType === "CSR"
          ? "This reads like a Clinical Summary Report."
          : probableDocType === "PSUR"
            ? "This reads like a Periodic Safety Update Report."
            : "Looks like you already have a document here.";

    return (
      <>
        <span className={styles.greetHi}>{greeting}</span>
        <div className={styles.bodyText}>
          {showAskAboutDocument
            ? "I can check its claims against your source documents, flag gaps or unsupported statements, or surface newer published evidence. Or we can start a fresh draft together."
            : "We can start a fresh draft together, section by section, with every claim cited to your sources."}
        </div>

        <div className={styles.buttonContainer}>
          {/* Gated on ``show.incomplete-actions`` — see showAskAboutDocument. */}
          {showAskAboutDocument && (
            <button type="button" className={styles.primaryBtn} onClick={onAskAboutDocument}>
              Ask about this document
            </button>
          )}
          {/* Promoted to the primary style when it is the only CTA left, so
              the card never renders without a primary action. */}
          <button
            type="button"
            className={showAskAboutDocument ? styles.secondaryBtn : styles.primaryBtn}
            onClick={onStartFreshDraft}
          >
            ＋ Start a fresh draft
          </button>
        </div>

        <div className={styles.disc}>
          Drafts are AI-generated; every claim cites a source. You review and sign off before anything ships.
        </div>
      </>
    );
  }

  // --- Empty doc → 3-step guided checklist ---
  // Derive step states from the two booleans. Each step is exactly one
  // of: done (green ✓), active (purple, actionable), or todo (locked).
  const isDoctypeSelected = hasDoctype;
  const isLibrarySelectionLocked = !hasDoctype;
  const isLibrarySelected = hasDoctype && hasLibrary;
  const isStartDraftLocked = !hasDoctype || !hasLibrary;

  return (
    <>
      <span className={styles.greetHi}>Welcome — let's build your first draft.</span>
      {isDoctypeSelected && !isLibrarySelected
        ? "One more step and we're ready to go."
        : "I'm your AI co-author. Three steps and I'll start writing, section by section, with every claim cited to your sources."}

      <div className={styles.stepsContainer}>
        {/* Step 1: Select Document Type */}
        <div className={styles.step}>
          <div className={mergeClasses(styles.stepN, isDoctypeSelected ? styles.stepNDone : styles.stepNActive)}>
            {isDoctypeSelected ? "✓" : "1"}
          </div>
          <div className={styles.stepContent}>
            <div className={styles.stepH}>Select Document Type</div>
            {isDoctypeSelected && doctypeLabel ? (
              // Completed — green value in an editable pill. Clicking the
              // chip (or its pencil) re-opens the picker. Changing the
              // doctype clears the library (handled in the shell).
              <button
                type="button"
                className={styles.stepPickChip}
                onClick={onPickDoctype}
                aria-label={`Change document type — currently ${doctypeLabel}`}
              >
                <span className={styles.stepPickValue}>{doctypeLabel}</span>
                <PencilIcon className={styles.stepPickIcon} />
              </button>
            ) : (
              <div className={styles.stepSub}>CER, CSR, PSUR…</div>
            )}
            {!isDoctypeSelected && (
              <button type="button" className={styles.stepGo} onClick={onPickDoctype}>
                Select document type →
              </button>
            )}
          </div>
        </div>

        {/* Step 2: Select Library */}
        <div className={styles.step}>
          <div
            className={mergeClasses(
              styles.stepN,
              isLibrarySelectionLocked ? styles.stepNTodo : isLibrarySelected ? styles.stepNDone : styles.stepNActive,
            )}
          >
            {isLibrarySelected ? "✓" : "2"}
          </div>
          <div className={styles.stepContent}>
            <div className={mergeClasses(styles.stepH, isLibrarySelectionLocked ? styles.stepHDim : undefined)}>
              Select Library
            </div>
            {isLibrarySelected && libraryName ? (
              // Completed — green value in an editable pill. Swaps the
              // library without touching the doctype.
              <button
                type="button"
                className={styles.stepPickChip}
                onClick={onPickLibrary}
                aria-label={`Change library — currently ${libraryName}`}
              >
                <span className={styles.stepPickValue}>{libraryName}</span>
                <PencilIcon className={styles.stepPickIcon} />
              </button>
            ) : (
              <div className={styles.stepSub}>The source files I&apos;ll draw from.</div>
            )}
            {!isLibrarySelectionLocked && !isLibrarySelected && (
              <button type="button" className={styles.stepGo} onClick={onPickLibrary}>
                Select library →
              </button>
            )}
          </div>
        </div>

        {/* Step 3: Start & Review Draft */}
        <div className={styles.step}>
          <div className={mergeClasses(styles.stepN, isStartDraftLocked ? styles.stepNTodo : styles.stepNActive)}>
            3
          </div>
          <div className={styles.stepContent}>
            <div className={mergeClasses(styles.stepH, isStartDraftLocked ? styles.stepHDim : undefined)}>
              Start &amp; Review Draft
            </div>
            {isStartDraftLocked ? (
              <div className={styles.stepSub}>Available once a doctype and library are set.</div>
            ) : (
              <>
                <div className={styles.stepSub}>
                  I&apos;ll draft section by section — review and sign off as it goes.
                </div>
                <button type="button" className={styles.stepGo} onClick={onReviewAndStart}>
                  Start drafting →
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    background: surfaceColors.paper,
    padding: "14px 0 120px",
    fontFamily: fonts.body,
    // Positioning context for the absolute thread line below.
    position: "relative",
  },
  // Phase 8.2 — chat-style today stream. Bubbles flow oldest→newest
  // (top→bottom) toward the composer below, matching the Variant D
  // mental model where the latest exchange sits closest to the input.
  // Each bubble row is a clickable wrapper so the writer can expand
  // the underlying entry into TimelineRowDetail (Phase 8.4 will move
  // that into Inspector).
  chatStream: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: `10px ${spacing.gutter} 0`,
  },
  // Live/transient blocks below the day stream (pinned card, approval,
  // live step timeline, draft bubble, proposals): same gutter and gap as
  // the stream, so nothing in the tail sits against the pane edge.
  tailStream: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: `10px ${spacing.gutter} 0`,
  },
  // No hover backdrop — the bubbles themselves are the visual unit;
  // a row-level shade behind them read as a phantom third surface.
  // Hover affordances live on the label-row action buttons instead.
  // A stretch of activity rows: tighter than the message gap so the lines
  // read as one sequence.
  activityRun: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  activityToggle: {
    alignSelf: "flex-start",
    background: "transparent",
    border: "none",
    padding: "2px 2px 2px 15px",
    cursor: "pointer",
    fontFamily: fonts.mono,
    fontSize: typeScale.meta,
    color: brandColors.primary,
    "&:hover": { textDecoration: "underline" },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
      borderRadius: "3px",
    },
  },
  chatBubbleRow: {
    display: "block",
    background: "transparent",
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
      borderRadius: "3px",
    },
  },
  chatBubbleRowCursor: {
    background: brandColors.light,
    border: `1.5px solid ${brandColors.primary}`,
    borderRadius: "3px",
  },
  // Subtle "this row's drawer is open" treatment so the writer
  // sees which bubble the bottom drawer is attached to.
  chatBubbleRowExpanded: {
    background: surfaceColors.paperDim,
    borderRadius: "3px",
  },
  // Sentinel anchor used by the auto-scroll effect — empty div pinned
  // at the bottom of the scrollable region; scrollIntoView lands here
  // whenever the message tail grows.
  bottomAnchor: {
    height: "1px",
    width: "100%",
  },
  // "New activity" pill: sits at the bottom of the visible chat while the
  // writer is scrolled up and the agent adds something below.
  jumpWrap: {
    position: "sticky",
    bottom: "10px",
    display: "flex",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 2,
    height: 0,
    overflow: "visible",
  },
  jumpButton: {
    pointerEvents: "auto",
    transform: "translateY(-100%)",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "999px",
    border: `1px solid ${brandColors.mid}`,
    background: brandColors.primary,
    color: "var(--on-brand)",
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
    ":hover": { filter: "brightness(1.08)" },
    "&:focus-visible": { outline: `2px solid ${brandColors.mid}`, outlineOffset: "2px" },
  },
  // Top-of-scroll sentinel observed by the pagination
  // IntersectionObserver — entering the (rootMargin-expanded)
  // viewport triggers ``onLoadOlder``. Rendered only while older
  // pages exist.
  topSentinel: {
    height: "1px",
    width: "100%",
  },
  // Transient "Loading earlier messages…" row shown at the top while
  // an older page is in flight.
  loadingOlderRow: {
    textAlign: "center",
    padding: "8px 0",
    fontFamily: fonts.mono,
    fontSize: typeScale.label,
    color: inkColors.ink4,
  },
  // Sticky day banner — pins to the top of the scrollport while the
  // writer scrolls through that day's messages; the next day's banner
  // pushes it out (each is confined to its own <section> containing
  // block, so the handoff is native CSS).
  // Transparent strip — only the pill reads as the banner; messages
  // scroll past around it (floating-pill treatment) instead of being
  // wiped by a full-width band.
  dayBanner: {
    position: "sticky",
    top: "0",
    zIndex: 3,
    display: "flex",
    justifyContent: "center",
    padding: "4px 0 6px",
    background: "transparent",
    pointerEvents: "none",
  },
  // At rest the pill is invisible; it fades in while the writer
  // scrolls and fades back out once scrolling settles (see
  // ``DAY_BANNER_IDLE_MS``). Opacity, not mount/unmount — the sticky
  // element must stay in flow or the handoff between day sections
  // would jump.
  dayBannerPill: {
    fontFamily: fonts.mono,
    fontSize: typeScale.label,
    fontWeight: 700,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "10px",
    padding: "2px 10px",
    opacity: 0,
    transform: "translateY(-4px)",
    transitionProperty: "opacity, transform",
    transitionDuration: "220ms",
    transitionTimingFunction: "ease",
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "0ms",
      transform: "none",
    },
  },
  dayBannerPillVisible: {
    opacity: 1,
    transform: "translateY(0)",
  },
  // Phase 8.3 — visible scope indicator shown above the chat stream
  // whenever ``filterSectionLabel`` is set. Tells the writer the
  // chat is narrowed; without this, missing messages look like a bug.
  filterBanner: {
    margin: "0 12px 6px",
    padding: "6px 10px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
    fontFamily: fonts.mono,
    fontSize: typeScale.label,
    color: inkColors.ink3,
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  filterBannerLabel: {
    color: brandColors.primary,
    fontWeight: 700,
  },
  // Spec 17 P6.3 — vertical thread line connecting all event nodes.
  empty: {
    padding: "40px 16px",
    textAlign: "center",
    color: inkColors.ink4,
    fontSize: typeScale.body,
    lineHeight: 1.5,
    maxWidth: "320px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  // Spec 29 — idle greeting surface. Conversational (not a strip),
  // left-aligned, body-scale text with a subtle brand-mid border
  // that echoes the chat-reply grammar. Each paragraph (split on
  // \n\n in buildIdleGreeting) renders as its own line for natural
  // cadence.
  // Greeting frame moved to ``AsthraBubbleSurface`` (Phase 5.1,
  // 2026-04-26). The greeting now wraps its text in that shared
  // wrapper; the only TimelineView-local concern is the outer
  // margin (chat-flow positioning) and inner padding + text wrap.
  greetingOuter: {
    margin: 0,
  },
  greetingBubble: {
    padding: "12px 14px",
    fontSize: typeScale.body,
    whiteSpace: "pre-wrap",
  },
  // Spec 29 — ephemeral "Thinking…" row shown at the top
  // of the timeline (newest-first; top = newest) while a chat
  // request is streaming. Not an EventLog entry — this is derived
  // from hook state (``useChatBridge.isStreaming``) and disappears
  // when the ``complete`` event lands (which in turn emits a real
  // askwhy row into the log). Drawn as a quiet assistant line where
  // the reply will appear: a small "Assistant" label, three dots and
  // the current step in plain words — no box, no code font, no tag.
  streamingRow: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "4px 2px",
    fontFamily: fonts.body,
  },
  streamingWho: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    color: inkColors.ink3,
  },
  streamingLine: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
    fontSize: typeScale.body,
    color: inkColors.ink3,
  },
  streamingText: { minWidth: 0, overflowWrap: "anywhere" },
  streamingIntent: {
    flexShrink: 0,
    fontSize: "11px",
    fontWeight: 500,
    lineHeight: "16px",
    padding: "0 7px",
    borderRadius: "999px",
    color: brandColors.primary,
    background: brandColors.light,
  },
  streamingTime: {
    marginLeft: "auto",
    flexShrink: 0,
    fontSize: "12px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
  },
  // "Draft so far" bubble — assistant-styled surface for mid-stream
  // text. Slightly dimmed vs a final reply so it reads as in-progress.
  draftBubble: {
    padding: spacing.bubblePadding,
    fontSize: typeScale.body,
    whiteSpace: "pre-wrap",
    opacity: 0.85,
  },
  streamingDots: { display: "inline-flex", gap: "3px", flexShrink: 0 },
  streamingDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    background: brandColors.primary,
    opacity: 0.25,
    animationName: {
      "0%": { opacity: 0.25 },
      "40%": { opacity: 1 },
      "80%": { opacity: 0.25 },
      "100%": { opacity: 0.25 },
    },
    animationDuration: "1200ms",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    ":nth-child(2)": { animationDelay: "150ms" },
    ":nth-child(3)": { animationDelay: "300ms" },
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      opacity: 0.6,
    },
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Elapsed-seconds counter for the streaming status row. Owns its own
 *  1s interval so the re-render stays scoped to this span — the rest
 *  of the timeline doesn't re-render on every tick. */
const ElapsedSince: React.FC<{ start: number; className?: string }> = ({ start, className }) => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const secs = Math.max(0, Math.round((nowMs - start) / 1000));
  // Quick answers need no clock; the count appears once a wait is noticeable.
  if (secs < 5) return null;
  return (
    <span className={className} aria-hidden="true">
      {secs}s
    </span>
  );
};

/**
 * Splits the streaming status into the words shown and the intent chip.
 * "[Edit highlight] Agent is searching the library…" reads
 * { intent: "Edit highlight", text: "Searching the library" }; a plain
 * chat message carries no chip.
 */
export function splitStreamingStatus(status: string): { intent: string | null; text: string } {
  const m = /^\[([^\]]+)\]\s*/.exec(status);
  const tag = m ? m[1].trim() : null;
  let text = (m ? status.slice(m[0].length) : status).trim();
  text = text.replace(/^Agent is\s+/i, "").replace(/\s*(…|\.\.\.)$/, "");
  if (text) text = text.charAt(0).toUpperCase() + text.slice(1);
  return { intent: tag && tag.toLowerCase() !== "chat" ? tag : null, text: text || "Thinking" };
}

export interface TimelineViewProps {
  /** Entries already in newest-first order. Caller (shell) handles
   *  reversal + filtering; TimelineView just renders. */
  entries: readonly EventLogEntry[];
  /** Optional section label for the empty-state copy when a filter
   *  is active (e.g. "§9.2" → "No events for §9.2 yet"). Null means
   *  no filter; use the generic empty copy. */
  filterSectionLabel?: string | null;
  /** Spec 17 P6.1 — index into ``entries`` that the writer's
   *  ``/replay`` walk has reached. When non-null, that row renders
   *  with a highlight ring and scrollIntoView on each cursor
   *  change. Null means no replay active; no highlight, normal flow. */
  replayCursor?: number | null;
  /** Spec 24 T1 — current run id, threaded into TimelineRowDetail
   *  so per-kind fetchers (T3+) have run scope. Null when no run. */
  runId?: string | null;
  /** Spec 24 T3 — verdict lookup by sectionId for ApprovalDetail.
   *  Shell passes a closure over its useRunVerdictWatcher state;
   *  TimelineView passes through unchanged. Optional so legacy
   *  callers / pre-T3 paths still render with degraded approval
   *  detail (honest "verdict no longer in memory" note). */
  verdictForSection?: (sectionId: string) => import("../hooks/useRunVerdictWatcher").VerdictRecord | null;
  /** Spec 29 — ephemeral streaming status ("Thinking…"
   *  or "Asthra is searching literature…") shown as a transient
   *  row at the top of the timeline while a chat request is in
   *  flight. Null hides the row. Not written to the EventLog —
   *  this is hook-state-derived and disappears when the paired
   *  askwhy arrives. */
  streamingStatus?: string | null;
  /** Mid-stream assistant text ("draft so far") from ``text`` SSE
   *  events, rendered as a transient assistant-styled bubble beneath
   *  the streaming row. Replaced by the real askwhy entry on
   *  ``complete``. Null hides the bubble. */
  streamingDraft?: string | null;
  /** Epoch ms when the in-flight chat request started. Non-null
   *  renders an elapsed-seconds counter next to the streaming
   *  status so long setups read as alive, not stalled. */
  streamStartedAt?: number | null;
  /** Spec 29 Phase 2 — pending approval surfaced inline at the top
   *  of the timeline when a section needs the writer's sign-off.
   *  Null hides the row. Same ``state.pendingApproval`` the
   *  spine-side ApprovalPopout reads, so both tabs stay in sync. */
  pendingApproval?: PendingApproval | null;
  /** Fired when the writer clicks "Mark resolved" on the inline
   *  approval row. Shell dispatches APPROVAL_RESOLVED. */
  onApprovalAccept?: () => void;
  /** Fired when the writer clicks "Open review" on the inline
   *  approval row. Shell focuses the section + opens Inspector's
   *  Review tab, then dispatches APPROVAL_RESOLVED. */
  onApprovalOpenReview?: () => void;
  /** Spec 29 Phase 3 — idle-state greeting. Shown instead of the
   *  generic "No events yet" empty-state when the writer hasn't
   *  chatted or run anything yet. Null → generic empty-state copy
   *  (the existing behaviour, for mid-run / post-run contexts
   *  where the greeting would be misleading). Can be a string or
   *  React component (for checklist/card greeting content). */
  greeting?: string | React.ReactNode | null;
  /** Spec: Blank-Document Onboarding — a PERSISTENT onboarding
   *  greeting (the IdleChecklist / ContextCard) that stays pinned at
   *  the bottom of the idle timeline as ONE interactive message
   *  REGARDLESS of how many events have landed. Unlike ``greeting``
   *  (which hides the instant the log is non-empty), this keeps the
   *  checklist mounted so the writer's pre-run picks update it in
   *  place — the completed step flips green without the message being
   *  torn down. Superseded by ``pinnedAgentCard`` when the
   *  conversational /draft flow is active. Null → not shown. */
  persistentGreeting?: React.ReactNode | null;
  /** Spec 29 Phase A conversational /draft — an arbitrary React
   *  node (typically AgentMessageChips) that pins to the top of
   *  the timeline. Hides the greeting while active; writers see
   *  the current conversational prompt instead. Null hides the
   *  card — default state. */
  pinnedAgentCard?: React.ReactNode;
  /** The mini spine (RunCapsule) for the current run, rendered IN the
   *  stream at ``at`` (the run's start), so it scrolls with the
   *  conversation: messages from before the run sit above it, anything
   *  after sits below. Null / undefined → no card (no run). */
  inlineCapsule?: {
    at: number;
    /** The card, or a render function handed the live batch's newest
     *  folded step (its status line), or null when it has none. */
    node: React.ReactNode | ((latestStep: RunStep | null) => React.ReactNode);
  } | null;
  /** The current run's latest batch once it has settled, for when the
   *  stream has its "Run started" note but no run-complete note to close it
   *  (a failed batch, or a restore that skipped the note). Its ribbon then
   *  reads from here. */
  settledBatch?: { summary: RunCompleteSummary | null; stopped: boolean } | null;
  /** Switch to the Spine tab (a finished batch's ribbon). */
  onOpenSpine?: () => void;
  /** Flat ascending persisted chat history for the current doc/run
   *  scope (from ``useChatHistoryView``). Merged with the live
   *  EventLog rows into one chronological, day-grouped stream —
   *  persisted copies of live messages are deduped by ``at:role``.
   *  Null/undefined renders the live stream only. */
  persistedMessages?: readonly ChatMessage[] | null;
  /** True when older pages exist on the backend before the oldest
   *  loaded message — renders the top scroll sentinel that triggers
   *  ``onLoadOlder``. */
  hasMoreHistory?: boolean;
  /** True while an older page is being fetched — shows the loading
   *  row at the top of the scroll. */
  isLoadingHistory?: boolean;
  /** Fetch + prepend the next older history page. Called by the top
   *  sentinel's IntersectionObserver; must be idempotent while in
   *  flight (the hook guards). */
  onLoadOlder?: () => Promise<void> | void;
  /** Phase 7.5 — current-time anchor used to render the sticky day
   *  banner labels ("Today" / "Yesterday" / weekday / date). Defaults
   *  to a fresh ``new Date()`` when omitted (degraded but workable). */
  now?: Date;
  /** The setup card's "Next" button — pick a library or start drafting. */
  onSetupNextStep?: (step: SetupCommand) => void;
  /** Called when the writer clicks the reply button on a message.
   *  The shell lifts this to set the pending quote in CommandRail. */
  onReply?: (message: ChatMessage) => void;
  /** Threaded to MessageBubble — records a proposal Insert outcome as an
   *  ``approval`` EventLog entry (shell owns the dispatch). */
  onProposalDecision?: (note: string, sectionId: string | null) => void;
  /** Chat "edit highlight" — the PENDING proposal rendered as the
   *  newest assistant message in the thread tail (drafted output +
   *  Approve/Reject). Transient: on decision the shell appends the
   *  durable ``edit_proposal`` entry and this node unmounts. Null →
   *  no proposal awaiting a decision. */
  proposalMessage?: React.ReactNode;
  /** In-flight tool-step activity boxes for the current chat turn
   *  (transient "running" rows). Resolved steps arrive as durable
   *  ``chat_tool_step`` entries through ``entries``, so this node only
   *  ever shows the step(s) still executing. Null when idle. */
  liveActivity?: React.ReactNode;
  /** The open document's name and folder for the setup card's rows.
   *  Absent → the card shows no name / folder rows. */
  setupDocument?: SetupDocumentInfo | null;
  /** Bumped by the shell after a full-pane overlay closes over the chat:
   *  repaint the chat and pin it to the newest message. 0 / absent → never. */
  pinToLatestSignal?: number;
}

export const TimelineView: React.FC<TimelineViewProps> = ({
  entries,
  filterSectionLabel,
  replayCursor = null,
  runId = null,
  verdictForSection,
  streamingStatus = null,
  streamingDraft = null,
  streamStartedAt = null,
  pendingApproval = null,
  onApprovalAccept,
  onApprovalOpenReview,
  greeting = null,
  persistentGreeting = null,
  pinnedAgentCard = null,
  inlineCapsule = null,
  settledBatch = null,
  onOpenSpine,
  persistedMessages = null,
  hasMoreHistory = false,
  isLoadingHistory = false,
  onLoadOlder,
  now,
  onSetupNextStep,
  onReply,
  onProposalDecision,
  proposalMessage = null,
  liveActivity = null,
  setupDocument = null,
  pinToLatestSignal = 0,
}) => {
  const styles = useStyles();
  const editSteps = useEditSteps();

  // Day-banner reveal. The banner is a scroll affordance, not
  // permanent chrome: it says where the writer is WHILE they move,
  // then gets out of the way ``DAY_BANNER_IDLE_MS`` after they stop.
  // Programmatic scrolls (initial bottom landing, auto-follow on live
  // growth, replay cursor) must not reveal it — that's the app
  // moving, not the writer — so each marks a suppression window here.
  // A WINDOW, not a boolean flag: a programmatic scroll that turns
  // out to be a no-op (already at the bottom) emits no scroll event,
  // and a boolean would have no event left to clear it — it would
  // swallow the writer's next real scroll. Declared above the scroll
  // effects that call it; a deps array evaluates during render, so a
  // later ``const`` would be a TDZ throw, not just a lint nit.
  const [dayBannerVisible, setDayBannerVisible] = useState(false);
  const scrollIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const programmaticUntilRef = useRef(0);
  const markProgrammaticScroll = useCallback(() => {
    programmaticUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_WINDOW_MS;
  }, []);
  useEffect(() => {
    return () => {
      if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
    };
  }, []);

  // Ref on the currently-cursored row so we can scrollIntoView when
  // the cursor moves. Null when no row is cursored. Keeping a SINGLE
  // ref (not a Map of refs) works because exactly one row carries
  // the cursor class at any time.
  const cursorRowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (replayCursor === null) return;
    markProgrammaticScroll();
    cursorRowRef.current?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  }, [replayCursor, markProgrammaticScroll]);

  // Phase 8.2 — auto-scroll to the bottom anchor whenever the chat
  // tail grows or transient state arrives (streaming, approval).
  // Skipped while a /replay walk is active (replayCursor != null) so
  // the cursor's scrollIntoView wins; otherwise the two would fight.
  // Pagination guards: nothing fires before the initial bottom
  // scroll, and a writer who has scrolled up to read history is not
  // yanked back down by live growth (older-page prepends never touch
  // these deps, so they can't trigger this either).
  const bottomAnchorRef = useRef<HTMLDivElement | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional scroll triggers
  useEffect(() => {
    if (replayCursor !== null) return;
    if (!didInitialScrollRef.current) return;
    if (!nearBottomRef.current) return;
    // Instant, not smooth: a smooth scroll still in flight when the next row
    // lands stops short of the new bottom.
    markProgrammaticScroll();
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries.length, streamingStatus, streamingDraft, pendingApproval, replayCursor]);

  // Clickable message behavior and detail drawer have been removed.
  // Messages are now non-interactive, read-only display only.

  // Streaming status row — rendered at the top whenever a chat
  // request is in flight. Appears above both the empty-state and
  // the first real entry, so the writer sees live activity even
  // when the log is otherwise empty.
  // While live chat steps show (``liveActivity``), their timeline header
  // is the status line; a second italic line would repeat the newest step.
  const streamingParts = streamingStatus ? splitStreamingStatus(streamingStatus) : null;
  const streamingRow =
    streamingStatus && !liveActivity ? (
      editSteps && editSteps.length > 0 ? (
        // Live edit steps → the step timeline; its header carries the live
        // state and timer, so the italic status line would only repeat the
        // newest step.
        // biome-ignore lint/a11y/useSemanticElements: layout requires div
        <div className={styles.chatBubbleRow} role="status" aria-live="polite">
          <ActionGroup count={editSteps.length} live startedAt={streamStartedAt} listLabel="Edit progress">
            {editSteps.map((step) => (
              <StepRow key={step.id} name={step.name} summary={step.summary} meta={step.meta} status={step.status} />
            ))}
          </ActionGroup>
        </div>
      ) : (
        // biome-ignore lint/a11y/useSemanticElements: layout requires div
        <div className={styles.streamingRow} role="status" aria-live="polite">
          <span className={styles.streamingWho}>Assistant</span>
          <div className={styles.streamingLine}>
            <span className={styles.streamingDots} aria-hidden="true">
              <span className={styles.streamingDot} />
              <span className={styles.streamingDot} />
              <span className={styles.streamingDot} />
            </span>
            <span className={styles.streamingText}>{streamingParts?.text}</span>
            {streamingParts?.intent && <span className={styles.streamingIntent}>{streamingParts.intent}</span>}
            {streamStartedAt !== null && <ElapsedSince start={streamStartedAt} className={styles.streamingTime} />}
          </div>
        </div>
      )
    ) : null;

  // "Draft so far" bubble — mid-stream assistant text from ``text``
  // SSE events. Legacy chat updates it once per tool-loop round; v3
  // grows it token-by-token. The real askwhy entry replaces it when
  // ``complete`` lands (the shell nulls ``streamingDraft`` then).
  const streamingDraftBubble = streamingDraft ? (
    // biome-ignore lint/a11y/useSemanticElements: AsthraBubbleSurface forwards role to its inner div
    <AsthraBubbleSurface className={styles.greetingOuter} bubbleClassName={styles.draftBubble} role="status">
      {streamingDraft}
    </AsthraBubbleSurface>
  ) : null;

  // Spec 29 Phase 2 — inline approval row. Appears above entries
  // when a section needs writer sign-off. Rendered only when
  // handlers are wired — without them, the row would have click
  // targets that dispatch nothing (dark UX).
  const approvalRow =
    pendingApproval && onApprovalAccept && onApprovalOpenReview ? (
      <InlineApprovalRow approval={pendingApproval} onAccept={onApprovalAccept} onOpenReview={onApprovalOpenReview} />
    ) : null;

  // Phase 8.2 — chronological order (oldest→newest) so the latest
  // exchange sits at the bottom near the composer. The shell hands
  // entries newest-first (legacy contract); we reverse a SHALLOW copy
  // here rather than mutating the prop. The reverse is a stable
  // chronological sort because the shell's source array is already
  // ts-sorted.
  const chronologicalEntries = React.useMemo(() => {
    return [...entries].reverse();
  }, [entries]);

  // Dedup key matches chatHistoryWiring.mergeMessages — ``(at, role)``
  // rather than ``at`` alone so paired user+assistant messages
  // dispatched in the same millisecond (suggestion path) don't
  // accidentally collide and hide one half of the pair from the
  // persisted-history rows. Tool rows also carry their name — two
  // different tool steps can share a millisecond within one turn.
  const liveMessageKeys = React.useMemo(() => {
    const out = new Set<string>();
    for (const entry of chronologicalEntries) {
      for (const message of projectToMessages(entry)) {
        out.add(timelineDedupKey(message));
      }
    }
    return out;
  }, [chronologicalEntries]);

  // Persisted history minus messages the live EventLog already
  // renders — the persisted copy of a live message would otherwise
  // show the same bubble twice (generalises the old restored-today
  // dedupe to every day).
  // Draft echoes saved by older builds are dropped too: the mini spine
  // now carries those runs.
  const persistedOnly = React.useMemo(() => {
    return withoutDraftEchoes(persistedMessages ?? []).filter(
      (message) => !liveMessageKeys.has(timelineDedupKey(message)),
    );
  }, [persistedMessages, liveMessageKeys]);

  // ``replayCursor`` is an index into the original newest-first
  // ``entries`` array. Translate it to the chronological array so the
  // highlight + scrollIntoView land on the right bubble.
  const chronologicalCursor = replayCursor === null ? null : entries.length - 1 - replayCursor;

  // The unified day-grouped stream: persisted rows + live entry rows,
  // ascending, one group per local calendar day. Persisted items are
  // pushed first so same-millisecond ties keep persisted-before-live
  // order (matches the old restored-then-live render).
  const capsuleAt = inlineCapsule ? inlineCapsule.at : null;
  const { dayGroups, latestLiveStep } = React.useMemo(() => {
    const items: TimelineItem[] = [];
    for (const message of persistedOnly) {
      items.push({
        kind: "message",
        at: message.at,
        key: (message as { _id?: string })._id ?? `p:${message.at}:${message.role}`,
        message,
      });
    }
    chronologicalEntries.forEach((entry, entryIndex) => {
      const projected = projectToMessages(entry);
      // An entry with nothing to say in chat (e.g. ``draft``, carried by the
      // mini spine) leaves no empty row behind.
      if (projected.length === 0) return;
      items.push({
        kind: "entry",
        at: projected[0]?.at ?? entry.ts,
        key: entry.id,
        entry,
        entryIndex,
      });
    });
    // Drafting batches: each "Run started" / run-complete pair becomes one
    // ribbon at the batch's start, in place of the two notes. The live
    // batch (still open) shows the mini spine there instead.
    const marks: BatchMark[] = [];
    const unbracketed = items.filter((item) => {
      const mark = batchMarkFor(item);
      if (mark) marks.push(mark);
      return mark === null;
    });
    const batches = pairRunBatches(marks);
    const last = batches[batches.length - 1];
    const liveBatch = capsuleAt !== null && last && last.endAt === null ? last : null;
    // The pre-draft setup exchange (document type, library, next-step
    // hint) becomes one setup card at its first step, updated in place.
    const setup = foldSetupRows(
      unbracketed,
      setupProbeFor,
      batches.map((b) => b.startAt),
    );
    // Every other row the batch posts (activation / trial-context notes,
    // its web searches) folds into the batch: the live card's status line,
    // or the finished ribbon's expanded list. The writer's messages and
    // Asthra's replies to them stay in chat.
    const { kept: stream, folded } = foldRunRows(setup.kept, batches, foldProbeFor, liveBatch?.key ?? null);
    for (const card of setup.cards) stream.push({ kind: "setup", at: card.at, key: card.key, card });
    const stepsFor = (key: string): RunStep[] => (folded.get(key) ?? []).flatMap(runStepsFor);
    if (capsuleAt !== null) {
      stream.push({ kind: "capsule", at: liveBatch ? liveBatch.startAt : capsuleAt, key: "run-capsule" });
    }
    for (const batch of batches) {
      if (batch !== liveBatch) {
        stream.push({ kind: "ribbon", at: batch.startAt, key: batch.key, batch, steps: stepsFor(batch.key) });
      }
    }
    const liveSteps = liveBatch ? stepsFor(liveBatch.key) : [];
    return {
      dayGroups: groupItemsByDay(stream, now ?? new Date()),
      latestLiveStep: liveSteps.length > 0 ? liveSteps[liveSteps.length - 1] : null,
    };
  }, [persistedOnly, chronologicalEntries, now, capsuleAt]);
  const lastBatchKey = React.useMemo(() => {
    let key: string | null = null;
    for (const group of dayGroups) for (const item of group.items) if (item.kind === "ribbon") key = item.key;
    return key;
  }, [dayGroups]);

  // ----- Scroll orchestration ---------------------------------------------
  // Four concerns share the container: (1) open scrolled to the
  // bottom, once; (2) stick to the bottom on live growth ONLY while
  // the writer is already near it; (3) preserve the viewport when an
  // older page prepends (manual adjustment — CSS overflow-anchor
  // does not anchor when scrollTop is at/near 0, which is exactly
  // the prepend case); (4) reveal the day banner while the writer
  // scrolls and hide it once they stop.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const nearBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const didInitialScrollRef = useRef(false);
  const pendingPrependRef = useRef<{
    prevScrollHeight: number;
    prevScrollTop: number;
  } | null>(null);

  // (4) reveals the day banner — see the block above the cursor
  // effect, which owns that state.
  // # DECISION: only the writer scrolling UP un-sticks the chat from the
  // #   bottom; the distance from the bottom alone never does.
  // # WHY: a big row landing mid smooth-scroll (or growing in place) put the
  // #   view >120px from the bottom on the app's own scroll events, so the
  // #   chat stopped following and parked on blank space behind a
  // #   "New activity" pill the writer had to click every time (2026-10-28).
  // #   Auto-follow only ever moves down, so an upward move is the writer.
  // # DATE: 2026-10-28
  const lastScrollTopRef = useRef(0);
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    const movedUp = el.scrollTop < lastScrollTopRef.current - 2;
    lastScrollTopRef.current = el.scrollTop;
    if (atBottom) {
      nearBottomRef.current = true;
      setShowJump(false);
    } else if (movedUp) {
      nearBottomRef.current = false;
    }

    if (Date.now() >= programmaticUntilRef.current) setDayBannerVisible(true);
    if (scrollIdleTimerRef.current) clearTimeout(scrollIdleTimerRef.current);
    scrollIdleTimerRef.current = setTimeout(() => setDayBannerVisible(false), DAY_BANNER_IDLE_MS);
  }, []);

  // # DECISION: follow the agent as the chat grows in place. The effect above
  // #   only fires on new entries; live steps (resolve/edit-highlight
  // #   progress, streaming lines) grow existing rows instead. A mutation
  // #   observer on the scroll container catches every growth: near the
  // #   bottom it keeps the view pinned; scrolled up it shows a
  // #   "New activity" pill instead of yanking the writer down.
  // # WHY: Guru (2026-10-14): "when the agent is trying to do something, it
  // #      doesn't auto-scroll down".
  // # DATE: 2026-10-14
  const replayCursorRef = useRef(replayCursor);
  replayCursorRef.current = replayCursor;
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || typeof MutationObserver === "undefined") return;
    let frame = 0;
    // Only real growth counts: banners and hover states also mutate the DOM.
    let lastHeight = el.scrollHeight;
    const obs = new MutationObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const grew = el.scrollHeight > lastHeight + 2;
        lastHeight = el.scrollHeight;
        if (!grew || replayCursorRef.current !== null || !didInitialScrollRef.current) return;
        if (nearBottomRef.current) {
          markProgrammaticScroll();
          el.scrollTop = el.scrollHeight;
        } else {
          setShowJump(true);
        }
      });
    });
    obs.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      obs.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [markProgrammaticScroll]);

  const jumpToLatest = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    markProgrammaticScroll();
    nearBottomRef.current = true;
    setShowJump(false);
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [markProgrammaticScroll]);

  // # DECISION: after a full-pane overlay closes (the Word-only review pop-up),
  // #   force the chat to repaint and pin it to the newest message.
  // # WHY: Word for Mac left the closed overlay's blank paper painted over the
  // #   chat — only rows that re-rendered (the "New activity" pill) showed;
  // #   scrolling brought the messages back (2026-10-25).
  // # DATE: 2026-10-25
  useEffect(() => {
    if (!pinToLatestSignal) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    // A one-frame compositing change invalidates the whole container's paint,
    // even when the scroll position below doesn't move.
    el.style.transform = "translateZ(0)";
    void el.offsetHeight;
    const frame = requestAnimationFrame(() => {
      el.style.transform = "";
    });
    markProgrammaticScroll();
    nearBottomRef.current = true;
    setShowJump(false);
    el.scrollTop = el.scrollHeight;
    return () => {
      cancelAnimationFrame(frame);
      el.style.transform = "";
    };
  }, [pinToLatestSignal, markProgrammaticScroll]);

  // (1) Initial bottom scroll — fires the first time the stream is
  // non-empty (covers the async persisted seed arriving after mount;
  // an entries-keyed effect would never fire for persisted-only
  // content). Instant, not smooth: the writer should open AT the
  // newest message, not watch a fly-by.
  const hasStreamContent = dayGroups.length > 0;
  useLayoutEffect(() => {
    if (didInitialScrollRef.current || !hasStreamContent) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    markProgrammaticScroll();
    el.scrollTop = el.scrollHeight;
    didInitialScrollRef.current = true;
  }, [hasStreamContent, markProgrammaticScroll]);

  // (3) Prepend anchor preservation. The IO callback records the
  // geometry before calling ``onLoadOlder``; when the first item key
  // changes (an older page landed) we restore the writer's viewport
  // by the height delta. A fetch that settles without prepending
  // (failure / empty page) drops the stale anchor instead.
  const firstItemKey = dayGroups[0]?.items[0]?.key ?? null;
  const prevFirstKeyRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const keyChanged = prevFirstKeyRef.current !== firstItemKey;
    prevFirstKeyRef.current = firstItemKey;
    const pending = pendingPrependRef.current;
    const el = scrollContainerRef.current;
    if (!pending || !el) return;
    if (keyChanged) {
      el.scrollTop = el.scrollHeight - pending.prevScrollHeight + pending.prevScrollTop;
      pendingPrependRef.current = null;
    } else if (!isLoadingHistory) {
      pendingPrependRef.current = null;
    }
  }, [firstItemKey, isLoadingHistory]);

  // Top sentinel observer — fires ``onLoadOlder`` when the writer
  // scrolls within 200px of the oldest loaded message. Attached only
  // while older pages exist (the sentinel unmounts on exhaustion)
  // and armed only after the initial bottom scroll so mounting at
  // scrollTop 0 doesn't trigger a spurious page. Guarded against the
  // jsdom test environment, which has no IntersectionObserver.
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root || !hasMoreHistory || !onLoadOlder) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (ioEntries) => {
        for (const ioEntry of ioEntries) {
          if (!ioEntry.isIntersecting) continue;
          if (!didInitialScrollRef.current) continue;
          if (isLoadingHistory) continue;
          const el = scrollContainerRef.current;
          if (el && pendingPrependRef.current === null) {
            pendingPrependRef.current = {
              prevScrollHeight: el.scrollHeight,
              prevScrollTop: el.scrollTop,
            };
          }
          void onLoadOlder();
        }
      },
      { root, rootMargin: "200px 0px 0px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreHistory, onLoadOlder, isLoadingHistory]);

  // Tail blocks shared by both render paths (idle + entries). Order:
  // pinned card > approval > streaming > greeting. Greeting only when
  // truly idle (no live messages, no active prompts/approvals).
  // The PERSISTENT onboarding message (IdleChecklist / ContextCard).
  // Unlike ``showGreeting`` below it does NOT depend on the log being
  // empty — once the writer picks a doctype/library and those events
  // land, the checklist must stay mounted so its steps flip to green
  // in place. Suppressed only when the conversational /draft card has
  // taken over the surface.
  const showPersistentGreeting = Boolean(persistentGreeting) && !pinnedAgentCard;

  // The EPHEMERAL greeting (fallback welcome text) — shown only on a
  // truly empty surface and hidden the instant anything else arrives.
  // When a persistent greeting is present it wins, so the two never
  // stack.
  const showGreeting =
    Boolean(greeting) &&
    !showPersistentGreeting &&
    !approvalRow &&
    !streamingRow &&
    !pinnedAgentCard &&
    !proposalMessage &&
    !inlineCapsule &&
    chronologicalEntries.length === 0 &&
    persistedOnly.length === 0;
  const showTailGreeting = showGreeting || showPersistentGreeting;
  const hasTail = Boolean(
    pinnedAgentCard ||
      approvalRow ||
      liveActivity ||
      streamingRow ||
      streamingDraftBubble ||
      proposalMessage ||
      showTailGreeting,
  );
  const tailBlocks = hasTail ? (
    <div className={styles.tailStream}>
      {pinnedAgentCard}
      {approvalRow}
      {liveActivity}
      {streamingRow}
      {streamingDraftBubble}
      {proposalMessage}
      {showTailGreeting && (
        // biome-ignore lint/a11y/useSemanticElements: AsthraBubbleSurface forwards role to its inner div
        <AsthraBubbleSurface className={styles.greetingOuter} bubbleClassName={styles.greetingBubble} role="status">
          {showPersistentGreeting ? persistentGreeting : greeting}
        </AsthraBubbleSurface>
      )}
    </div>
  ) : null;

  const isFullyEmpty =
    chronologicalEntries.length === 0 &&
    !showGreeting &&
    !showPersistentGreeting &&
    !streamingRow &&
    !approvalRow &&
    !pinnedAgentCard &&
    !proposalMessage &&
    !inlineCapsule &&
    persistedOnly.length === 0;

  // Phase 8.3 — filter scope banner. Visible whenever a section
  // filter is active so the writer knows why the chat tail is shorter
  // than expected. Other filter dimensions (kind, actor, sinceMs) are
  // not surfaced here yet — caller currently only passes sectionId.
  const filterBanner = filterSectionLabel ? (
    // biome-ignore lint/a11y/useSemanticElements: layout requires div
    <div className={styles.filterBanner} role="status" aria-label={`Filter active: ${filterSectionLabel}`}>
      <span>Filter:</span>
      <span className={styles.filterBannerLabel}>{filterSectionLabel}</span>
      <span>— showing matching messages only.</span>
    </div>
  ) : null;

  // One timeline row. Persisted rows render the stored message
  // directly; live rows keep the /replay cursor + highlight
  // affordances of the old entries list.
  const isCursorItem = (item: TimelineItem): boolean =>
    item.kind === "entry" && chronologicalCursor === item.entryIndex;

  const renderItem = (item: TimelineItem): React.ReactNode => {
    if (item.kind === "ribbon") {
      const { batch } = item;
      // An open batch that is not live: it ended without a run-complete note.
      // The latest one reads the shell's settled run; any other stopped.
      const fromShell = batch.endAt === null && item.key === lastBatchKey ? settledBatch : null;
      return (
        <div key={item.key} className={styles.chatBubbleRow}>
          <RunRibbon
            summary={batch.summary ?? fromShell?.summary ?? null}
            stopped={batch.endAt === null && (fromShell ? fromShell.stopped : true)}
            onOpenSpine={onOpenSpine}
            steps={item.steps}
          />
        </div>
      );
    }
    if (item.kind === "setup") {
      return (
        <div key={item.key} className={styles.chatBubbleRow}>
          <SetupCard card={item.card} document={setupDocument} onNextStep={onSetupNextStep} />
        </div>
      );
    }
    if (item.kind === "capsule") {
      return (
        <div key={item.key} className={styles.chatBubbleRow}>
          {typeof inlineCapsule?.node === "function" ? inlineCapsule.node(latestLiveStep) : inlineCapsule?.node}
        </div>
      );
    }
    if (item.kind === "message") {
      return (
        <div key={item.key} className={styles.chatBubbleRow}>
          <MessageBubble
            message={item.message}
            onReply={onReply}
            onProposalDecision={onProposalDecision}
          />
        </div>
      );
    }
    const isCursor = chronologicalCursor === item.entryIndex;
    const messages = projectToMessages(item.entry);
    return (
      <div
        key={item.key}
        ref={isCursor ? cursorRowRef : undefined}
        className={mergeClasses(styles.chatBubbleRow, isCursor && styles.chatBubbleRowCursor)}
        aria-current={isCursor ? "step" : undefined}
      >
        {messages.map((m, mi) => (
          <MessageBubble
            key={`${item.entry.id}:${mi}`}
            message={m}
            onReply={onReply}
            onProposalDecision={onProposalDecision}
          />
        ))}
      </div>
    );
  };

  // A chat turn's tool step: live ``chat_tool_step`` entries, and the same
  // steps restored from history (role ``tool`` messages), so a reloaded
  // turn folds into the same "Took N steps" rail instead of loose strips.
  const isToolStepItem = (it: TimelineItem): boolean => {
    if (it.kind === "entry") return it.entry.payload.kind === "chat_tool_step";
    return it.kind === "message" && it.message.role === "tool" && !NON_STEP_TOOL_NAMES.has(it.message.name);
  };

  // An activity row: a user or agent action note (role ``system``), live or
  // restored from history.
  const isActivityItem = (it: TimelineItem): boolean => {
    if (it.kind === "message") return it.message.role === "system";
    if (it.kind !== "entry") return false;
    const projected = projectToMessages(it.entry);
    return projected.length > 0 && projected.every((m) => m.role === "system");
  };

  const renderStepRows = (steps: TimelineItem[]): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];
    for (const s of steps) {
      if (s.kind === "message") {
        const m = s.message;
        if (m.role !== "tool") continue;
        rows.push(
          <StepRow key={s.key} name={m.name} summary={m.summary} meta={m.meta} status={m.status} detail={m.detail} />,
        );
        continue;
      }
      if (s.kind !== "entry") continue;
      for (const [mi, m] of projectToMessages(s.entry).entries()) {
        if (m.role !== "tool") continue;
        rows.push(
          <StepRow
            key={`${s.entry.id}:${mi}`}
            name={m.name}
            summary={m.summary}
            meta={m.meta}
            status={m.status}
            detail={m.detail}
          />,
        );
      }
    }
    return rows;
  };

  // A run of consecutive persisted ``chat_tool_step`` entries is one turn's
  // steps: fold them into one "Took N steps" timeline, closed by default.
  // Consecutive activity rows are one stretch of work: kept tight together,
  // and a long stretch folds behind "N earlier activities".
  const renderTimelineItems = (items: TimelineItem[]): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    let buffer: TimelineItem[] = [];
    let activities: TimelineItem[] = [];
    const flush = () => {
      if (buffer.length === 0) return;
      const steps = buffer;
      // A completed find_impacted_sections step offers the panel for its document.
      const impactDocs: string[] = [];
      for (const st of steps) {
        const doc = st.kind === "entry" ? impactDocumentFromToolStep(st.entry.payload) : null;
        if (doc && !impactDocs.includes(doc)) impactDocs.push(doc);
      }
      out.push(
        <div key={`agrp-${steps[0].key}`} className={styles.chatBubbleRow}>
          <ActionGroup count={steps.length}>{renderStepRows(steps)}</ActionGroup>
          {impactDocs.map((doc) => (
            <OpenImpactedSectionsButton key={doc} documentName={doc} />
          ))}
        </div>,
      );
      buffer = [];
    };
    const flushActivities = () => {
      if (activities.length === 0) return;
      const run = activities;
      out.push(
        <ActivityRun key={`act-${run[0].key}`} hasCursor={run.some(isCursorItem)}>
          {run.map(renderItem)}
        </ActivityRun>,
      );
      activities = [];
    };
    for (const it of items) {
      if (isToolStepItem(it)) {
        flushActivities();
        buffer.push(it);
      } else if (isActivityItem(it)) {
        flush();
        activities.push(it);
      } else {
        flush();
        flushActivities();
        out.push(renderItem(it));
      }
    }
    flush();
    flushActivities();
    return out;
  };

  return (
    <div className={styles.root} ref={scrollContainerRef} onScroll={handleScroll}>
      {hasMoreHistory && (
        <div
          ref={topSentinelRef}
          className={styles.topSentinel}
          data-testid="history-top-sentinel"
          aria-hidden="true"
        />
      )}
      {isLoadingHistory && (
        // biome-ignore lint/a11y/useSemanticElements: layout requires div
        <div className={styles.loadingOlderRow} role="status" aria-live="polite">
          Loading earlier messages…
        </div>
      )}
      {filterBanner}
      {dayGroups.map((group) => (
        <section key={group.date.getTime()} aria-label={group.label}>
          {/* Presentational — the parent section's aria-label already
              announces the day, so the sticky pill stays decorative. */}
          <div className={styles.dayBanner} aria-hidden="true">
            <span
              className={mergeClasses(styles.dayBannerPill, dayBannerVisible && styles.dayBannerPillVisible)}
              data-testid="day-banner-pill"
              data-visible={dayBannerVisible ? "true" : "false"}
            >
              {group.label}
            </span>
          </div>
          {/* biome-ignore lint/a11y/useSemanticElements: layout requires div */}
          <div className={styles.chatStream} role="list" aria-label={`Conversation, ${group.label}`}>
            {renderTimelineItems(group.items)}
          </div>
        </section>
      ))}
      {tailBlocks}
      {isFullyEmpty && (
        // biome-ignore lint/a11y/useSemanticElements: layout requires div
        <div className={styles.empty} role="status">
          {filterSectionLabel
            ? `No events for ${filterSectionLabel} yet.`
            : "No events yet — the conversation fills as the run progresses."}
        </div>
      )}
      {/* EntryDetailDrawer removed — messages are now non-interactive */}
      <div ref={bottomAnchorRef} className={styles.bottomAnchor} aria-hidden="true" />
      {showJump && (
        <div className={styles.jumpWrap}>
          <button type="button" className={styles.jumpButton} onClick={jumpToLatest} data-testid="chat-jump-latest">
            ↓ New activity
          </button>
        </div>
      )}
    </div>
  );
};

export default TimelineView;

/** Rows a folded activity run keeps visible under its "earlier" toggle. */
const ACTIVITY_VISIBLE_TAIL = 2;
/** A run longer than this folds. */
const ACTIVITY_FOLD_AT = 4;

/**
 * One stretch of consecutive activity rows. Short stretches show in full;
 * a long one shows its latest rows under a "N earlier activities" toggle,
 * so a burst of notes never pushes the conversation out of view. Opens by
 * itself when the /replay cursor lands inside it.
 */
const ActivityRun: React.FC<{ children: React.ReactNode[]; hasCursor: boolean }> = ({ children, hasCursor }) => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const folds = children.length > ACTIVITY_FOLD_AT;
  const showAll = !folds || open || hasCursor;
  const hidden = children.length - ACTIVITY_VISIBLE_TAIL;
  return (
    <div className={styles.activityRun}>
      {folds && !hasCursor && (
        <button type="button" className={styles.activityToggle} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide earlier activity" : `${hidden} earlier activit${hidden === 1 ? "y" : "ies"}`}
        </button>
      )}
      {showAll ? children : children.slice(-ACTIVITY_VISIBLE_TAIL)}
    </div>
  );
};
// Status colours imported for future kind-specific accent styling;
// suppress unused warning without deleting the import.
void statusColors;
// EventKind export reserved for P5's filter grammar.
export type { EventKind };

/** The batch bracket a stream row carries: a "Run started" note, or a
 *  run-complete note with its numbers (live payload, or parsed from the
 *  persisted note's text). Null for every other row. */
function batchMarkFor(item: TimelineItem): BatchMark | null {
  if (item.kind === "entry") {
    const payload = item.entry.payload;
    const runId = item.entry.runId ?? null;
    if (payload.kind === "run_start") return { kind: "start", at: item.at, key: item.key, runId };
    if (payload.kind === "run_complete") {
      const { totalSections, draftedSections, flaggedSections, durationMs } = payload;
      return {
        kind: "complete",
        at: item.at,
        key: item.key,
        runId,
        summary: { totalSections, draftedSections, flaggedSections, durationMs },
      };
    }
    return null;
  }
  if (item.kind !== "message" || item.message.role !== "system") return null;
  const text = (item.message as { text?: string }).text ?? "";
  const runId = item.message.run_id ?? null;
  if (text === RUN_STARTED_TEXT) return { kind: "start", at: item.at, key: item.key, runId };
  const summary = parseRunCompleteText(text);
  return summary ? { kind: "complete", at: item.at, key: item.key, summary, runId } : null;
}

/** How the setup fold reads a stream row: live entries by their kind,
 *  persisted messages by the templates that wrote them. */
function setupProbeFor(item: TimelineItem): SetupProbe | null {
  if (item.kind === "message") return setupProbeForMessage(item.message);
  if (item.kind === "entry") return setupProbeForEntry(item.entry);
  return null;
}

/** How the batch fold reads a stream row: who it is from, and its run. */
function foldProbeFor(item: TimelineItem): FoldProbe {
  if (item.kind === "message") {
    const m = item.message;
    const runId = m.run_id ?? null;
    if (m.role === "user") return { role: "writer", runId };
    if (m.role === "assistant") {
      const plainNote = !m.intent && !m.insertTarget && !m.editDecision && !m.isError;
      return { role: plainNote ? "note" : "reply", runId };
    }
    // Only the run's web searches fold; chat turns' tool steps stay with
    // the reply they belong to.
    if (m.role === "tool" && m.name === "internet_search") return { role: "tool", runId };
    return { role: "other", runId };
  }
  if (item.kind !== "entry") return { role: "other", runId: null };
  const { entry } = item;
  const runId = entry.runId ?? null;
  if (entry.payload.kind === "agent_note") return { role: "note", runId };
  if (entry.payload.kind === "web") return { role: "tool", runId };
  const roles = projectToMessages(entry).map((m) => m.role);
  if (roles.includes("user")) return { role: roles.includes("assistant") ? "reply" : "writer", runId };
  if (roles.includes("assistant")) return { role: "reply", runId };
  return { role: "other", runId };
}

/** Restored ``tool`` rows that are not a chat turn's step: run web searches
 *  (folded into run batches) and one-off citation / library notes. */
const NON_STEP_TOOL_NAMES: ReadonlySet<string> = new Set(["internet_search", "citation.swap", "library.update"]);

/** A folded row as the steps the card / ribbon list. */
function runStepsFor(item: TimelineItem): RunStep[] {
  if (item.kind === "message") return [runStepFromMessage(item.key, item.message)];
  if (item.kind !== "entry") return [];
  return projectToMessages(item.entry).map((m, i) => runStepFromMessage(`${item.key}:${i}`, m));
}
